/**
 * Agent Type utility
 *
 * Provides a single source of truth (localStorage) for the globally selected
 * agent type. Used by:
 *   1. The header AgentTypeDropdown to read/write the selection.
 *   2. The HTTP layer (AxiosAdapter, utils/request) to inject `agent_type`
 *      as a query parameter on every outgoing API call when a value is set.
 *
 * Why localStorage?
 *   The HTTP layer is plain TypeScript and lives outside the React tree, so it
 *   cannot use hooks/Redux selectors directly. localStorage is synchronously
 *   readable from anywhere, persists across reloads, and stays in sync with
 *   the Redux store via the AgentTypeDropdown change handler.
 */

import { AGENT_TYPE_INCLUDED_PATHS } from 'types/agentTypeAgnosticPaths';

export {
  AGENT_TYPE_INCLUDED_PATHS,
} from 'types/agentTypeAgnosticPaths';

export const AGENT_TYPE_STORAGE_KEY = 'selected_agent_type';

/**
 * Custom DOM event fired whenever the agent type changes.
 * Listeners (sagas, hooks, etc.) can subscribe via `window.addEventListener`.
 */
export const AGENT_TYPE_CHANGE_EVENT = 'agent-type-change';

/**
 * Backend-facing enum values. Keep these in sync with the API contract.
 * Labels (UI) live alongside the dropdown component.
 */
export const AGENT_TYPE_VALUES = {
  UNSOLD_NURTURING: 'UNSOLD_NURTURING',
  SOLD_NURTURING: 'SOLD_NURTURING',
  SERVICE_NURTURING: 'SERVICE_NURTURING',
  LEASE_RETURN_NURTURING: 'LEASE_RETURN_NURTURING',
} as const;

export type AgentTypeValue =
  (typeof AGENT_TYPE_VALUES)[keyof typeof AGENT_TYPE_VALUES];

const VALID_AGENT_TYPES: ReadonlySet<string> = new Set(
  Object.values(AGENT_TYPE_VALUES),
);

/**
 * Boundary-aware fragment match. Returns true if `pathOnly` contains
 * `fragment` followed by end-of-path or a `/` (i.e. matches a whole path
 * segment, not a prefix of a longer one).
 */
const matchesPathFragment = (pathOnly: string, fragment: string): boolean => {
  const idx = pathOnly.indexOf(fragment);
  if (idx === -1) return false;
  const nextChar = pathOnly.charAt(idx + fragment.length);
  return nextChar === '' || nextChar === '/';
};

/**
 * Returns true when the given URL should receive `agent_type` based on the
 * include-list in `types/agentTypeAgnosticPaths.ts`.
 *
 * Match is boundary-aware (end-of-string or next `/`); query string + hash
 * are stripped before matching.
 */
export const shouldIncludeAgentTypeInUrl = (url: string): boolean => {
  if (!url) return false;
  const pathOnly = url.split('?')[0].split('#')[0];

  return AGENT_TYPE_INCLUDED_PATHS.some(fragment =>
    matchesPathFragment(pathOnly, fragment),
  );
};

/**
 * Backward-compatible helper: returns true for URLs where `agent_type` should
 * NOT be injected.
 */
export const isAgentTypeAgnosticUrl = (url: string): boolean =>
  !shouldIncludeAgentTypeInUrl(url);

/**
 * Reads the currently selected agent type from localStorage.
 * Returns `null` when nothing is selected or when the stored value is invalid.
 */
export const getAgentType = (): AgentTypeValue | null => {
  try {
    if (typeof window === 'undefined' || !window.localStorage) {
      return null;
    }
    const stored = window.localStorage.getItem(AGENT_TYPE_STORAGE_KEY);
    if (!stored) return null;
    return VALID_AGENT_TYPES.has(stored) ? (stored as AgentTypeValue) : null;
  } catch (error) {
    console.error('getAgentType error:', error);
    return null;
  }
};

/**
 * Persists the selected agent type. Pass `null` (or empty string) to clear.
 * Fires `AGENT_TYPE_CHANGE_EVENT` so listeners can react (e.g. refetch data).
 */
export const setAgentType = (value: AgentTypeValue | null | ''): void => {
  try {
    if (typeof window === 'undefined' || !window.localStorage) {
      return;
    }
    if (!value) {
      window.localStorage.removeItem(AGENT_TYPE_STORAGE_KEY);
    } else if (VALID_AGENT_TYPES.has(value)) {
      window.localStorage.setItem(AGENT_TYPE_STORAGE_KEY, value);
    } else {
      console.warn('setAgentType: ignoring unknown value', value);
      return;
    }
    window.dispatchEvent(
      new CustomEvent(AGENT_TYPE_CHANGE_EVENT, {
        detail: { value: value || null },
      }),
    );
  } catch (error) {
    console.error('setAgentType error:', error);
  }
};

/**
 * Appends `agent_type=<value>` to a URL string when a value is selected.
 * Safe for both absolute and relative URLs and preserves existing params.
 * Used by utils/request.ts where the request is made via raw fetch/axios.
 *
 * No-ops for URLs that are not in `AGENT_TYPE_INCLUDED_PATHS`.
 */
export const appendAgentTypeToUrl = (url: string): string => {
  const agentType = getAgentType();
  if (!agentType || !url) return url;
  if (!shouldIncludeAgentTypeInUrl(url)) return url;

  try {
    // For relative URLs, attach a dummy origin so URL can parse them.
    const isAbsolute = /^https?:\/\//i.test(url);
    const base = isAbsolute ? undefined : 'http://__placeholder__';
    const parsed = new URL(url, base);

    // Avoid overriding an explicitly-passed agent_type (caller wins).
    if (!parsed.searchParams.has('agent_type')) {
      parsed.searchParams.set('agent_type', agentType);
    }

    return isAbsolute
      ? parsed.toString()
      : `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch (error) {
    console.error('appendAgentTypeToUrl error:', error);
    return url;
  }
};
